import {
  Tabs,
  TabsContent,
  TabsList,
  TabsScrollArea,
  TabsTrigger,
} from "~/components/ui/tabs";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Button } from "../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { NumberOfGuestsOptions } from "./SearchBar";
import partySeatingIcon from "~/assets/chairs/party.png";
import partySeatingIconSelected from "~/assets/chairs/party-selected.png";
import standingReceptionIcon from "~/assets/chairs/standing-reception.png";
import standingReceptionIconSelected from "~/assets/chairs/standing-reception-selected.png";
import blockIcon from "~/assets/chairs/block.png";
import blockIconSelected from "~/assets/chairs/block-selected.png";
import banquetIcon from "~/assets/chairs/banquet-seating.png";
import banquetIconSelected from "~/assets/chairs/banquet-seating-selected.png";
import rowIcon from "~/assets/chairs/row-seating.png";
import rowIconSelected from "~/assets/chairs/row-seating-selected.png";
import parliamentIcon from "~/assets/chairs/parliamentary-seating.png";
import parliamentIconSelected from "~/assets/chairs/parliamentary-seating-selected.png";
import uShapedIcon from "~/assets/chairs/u-shape.png";
import uShapedIconSelected from "~/assets/chairs/u-shape-selected.png";
import tableSeatingIcon from "~/assets/chairs/table-seating.png";
import tableSeatingIconSelected from "~/assets/chairs/table-seating-selected.png";
import { NumberOfGuests, SearchFilter } from "~/types/search";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { EventType } from "~/lib/server-functions/get-event-types";
import { LocationType } from "~/lib/server-functions/get-location-types";
import { InfraType } from "~/lib/server-functions/get-infra-types";
import { HotelType } from "~/lib/server-functions/get-hotel-types";
import { AmountFormatted } from "../AmountFormatted";
import iconAirport from "~/assets/distances/airport.png";
import iconCityCenter from "~/assets/distances/city-center.png";
import iconRailstation from "~/assets/distances/railstation.png";
import iconMotorway from "~/assets/distances/motorway.png";
import iconPublicTransport from "~/assets/distances/public-transport.png";
import { TrashIcon } from "~/icons/TrashIcon";
import { PinPlusIcon } from "~/icons/PinPlusIcon";
import { NoAccessIcon } from "~/icons/NoAccessIcon";
import { OneStarIcon } from "~/icons/OneStarIcon";
import { TwoStarsIcon } from "~/icons/TwoStarsIcon";
import { ThreeStarsIcon } from "~/icons/ThreeStarsIcon";
import { FourStarsIcon } from "~/icons/FourStarsIcon";
import { FiveStarsIcon } from "~/icons/FiveStarsIcon";
import { getYear } from "date-fns";
import { NumberInput } from "../ui/number-input";
import { IconSelect } from "~/lib/add-location/components/UIComponents";
import { useMemo, useState, useEffect } from "react";
import { LocationSearchInput } from "./LocationSearchInput";
import { PlaceDetails } from "~/lib/server-functions/get-place-detail";
import { X } from "lucide-react";
import { PinIcon } from "~/icons/PinIcon";
import {
  addLocationSelection,
  removeLocationSelectionAt,
} from "~/lib/search/location-place-ids";

type SearchFilterDialogValue = {
  filters: SearchFilter;
  guests: NumberOfGuests;
  type: number | null;
  location?: string[];
  /**
   * Place IDs aligned with `location` by index. Kept in sync on every add and
   * remove so a label never loses its Place ID and falls back to paid Geocoding.
   */
  placeIds?: string[];
  searchRadius?: number;
  locationIdOrName?: string;
};

const LOCATION_SEARCH_ACCORDION = "filter-location-search";

export function SearchFilterDialog({
  eventTypes,
  locationTypes,
  infraTypes,
  hotelTypes,
  trigger,
  value,
  numberOfMatchingLocations,
  onValueChange,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  expandAccordionOnOpen,
}: {
  eventTypes: (EventType & { locationCount: number })[];
  locationTypes: (LocationType & { locationCount: number })[];
  infraTypes: (InfraType & { locationCount: number })[];
  hotelTypes: (HotelType & { locationCount: number })[];
  trigger: React.ReactElement<typeof DialogTrigger>;
  value: SearchFilterDialogValue;
  numberOfMatchingLocations: number;
  onValueChange: (value: SearchFilterDialogValue) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  expandAccordionOnOpen?: string;
}) {
  type RentalDay = SearchFilter["rentalDuration"]["days"][number];

  // Track dialog open state (used when uncontrolled)
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isControlled =
    controlledOpen !== undefined && controlledOnOpenChange !== undefined;
  const isDialogOpenEffective = isControlled ? controlledOpen : isDialogOpen;
  const setDialogOpen = isControlled ? controlledOnOpenChange : setIsDialogOpen;

  // Track manually closed accordions (user closed them, so don't auto-open)
  const [manuallyClosedAccordions, setManuallyClosedAccordions] = useState<
    string[]
  >([]);

  // Calculate which accordions should be open based on active filters
  const requiredOpenAccordions = useMemo(() => {
    const f = value.filters ?? {};
    const accordionItems = [
      "filter-location-search",
      "filter-occasion-and-location-type",
      "filter-guests-and-seating",
      "filter-location-properties",
      "filter-parking-spaces",
      "filter-budget",
      "filter-contract-bindings",
      "filter-rental-duration",
      "filter-location-and-infrastructure",
      "filter-delivery-logistics",
      "filter-leisure-sport-wellness",
      "filter-certifications",
      "filter-equipment",
      "filter-overnight-options",
    ];

    return accordionItems.filter((t) => {
      if (
        t === "filter-location-search" &&
        ((value.location?.length ?? 0) > 0 ||
          value.searchRadius ||
          (value.locationIdOrName?.trim() ?? "") !== "")
      ) {
        return true;
      } else if (
        t === "filter-occasion-and-location-type" &&
        ((f.occasion?.length ?? 0) > 0 ||
          (f.locationType?.length ?? 0) > 0 ||
          value.type !== null)
      ) {
        return true;
      } else if (
        t === "filter-guests-and-seating" &&
        (f.seating || (value.guests && value.guests !== "any"))
      ) {
        return true;
      } else if (
        t === "filter-location-properties" &&
        (f.locationSettings?.indoor ||
          f.locationSettings?.outdoor ||
          f.locationSettings?.mixed ||
          (f.locationSize?.indoorAreaTotal ?? 0) > 0 ||
          (f.locationSize?.maxHeight ?? 0) > 0 ||
          (f.locationOpening ?? 0) > 0 ||
          (f.locationRenovation ?? 0) > 0 ||
          f.commonProperties?.accessible ||
          f.commonProperties?.dimmable ||
          f.commonProperties?.daylight ||
          (f.outerAreaSize ?? 0) > 0 ||
          (f.numberOfNeededRooms ?? 0) > 0 ||
          (f.outerAreaSize ?? 0) > 0 ||
          f.stage)
      ) {
        return true;
      } else if (
        t === "filter-parking-spaces" &&
        ((f.numberOfParkingSpaces ?? 0) > 0 || f.parkingLots !== undefined)
      ) {
        return true;
      } else if (
        t === "filter-overnight-options" &&
        (f.hasAccommodations !== undefined ||
          (f.hotelRooms ?? 0) > 0 ||
          (f.hotelStars ?? 0) > 0 ||
          (f.hotelTypes?.length ?? 0) > 0)
      ) {
        return true;
      } else if (
        t === "filter-budget" &&
        f.budget &&
        (f.budget.from !== null || f.budget.to !== null)
      ) {
        return true;
      } else if (
        t === "filter-contract-bindings" &&
        f.cateringBinding !== undefined &&
        f.techBinding !== undefined &&
        (f.cateringBinding !== "" || f.techBinding !== "")
      ) {
        return true;
      } else if (
        t === "filter-location-and-infrastructure" &&
        ((f.infrastructure?.length ?? 0) > 0 || (f.distances?.length ?? 0) > 0)
      ) {
        return true;
      } else if (
        t === "filter-rental-duration" &&
        ((f.rentalDuration?.days?.length ?? 0) > 0 ||
          f.rentalDuration?.from !== null ||
          f.rentalDuration?.to !== null)
      ) {
        return true;
      } else if (
        t === "filter-delivery-logistics" &&
        (f.deliveryLogistics?.truckAccess ||
          f.deliveryLogistics?.carAccess ||
          f.deliveryLogistics?.freightElevator ||
          f.deliveryLogistics?.loadingRamp)
      ) {
        return true;
      } else if (
        t === "filter-leisure-sport-wellness" &&
        (f.leisureSportWellness?.leisure?.sightseeing ||
          f.leisureSportWellness?.sport?.golf ||
          f.leisureSportWellness?.sport?.tennis ||
          f.leisureSportWellness?.sport?.sailing ||
          f.leisureSportWellness?.sport?.ski ||
          f.leisureSportWellness?.wellness?.sauna ||
          f.leisureSportWellness?.wellness?.massage ||
          f.leisureSportWellness?.wellness?.indoorPool ||
          f.leisureSportWellness?.wellness?.outdoorPool)
      ) {
        return true;
      } else if (
        t === "filter-certifications" &&
        (f.certifications?.gstcHotel ||
          f.certifications?.greenKey ||
          f.certifications?.iso14001)
      ) {
        return true;
      } else if (
        t === "filter-equipment" &&
        (f.equipment?.acPower?.volt110 ||
          f.equipment?.acPower?.volt230 ||
          f.equipment?.threePhasePower?.amp16 ||
          f.equipment?.threePhasePower?.amp32 ||
          f.equipment?.threePhasePower?.amp64 ||
          f.equipment?.threePhasePower?.amp128 ||
          f.equipment?.technology?.lighting ||
          f.equipment?.technology?.presentationVideo ||
          f.equipment?.technology?.audio ||
          f.equipment?.technology?.heatingClimate ||
          f.equipment?.specialTechnology?.rigging ||
          f.equipment?.specialTechnology?.v1 ||
          f.equipment?.specialTechnology?.v2 ||
          f.equipment?.specialTechnology?.v3 ||
          f.equipment?.specialTechnology?.v4 ||
          f.equipment?.specialTechnology?.v5 ||
          f.equipment?.internet?.g5 ||
          f.equipment?.internet?.g4 ||
          f.equipment?.internet?.g3 ||
          f.equipment?.internet?.g2 ||
          (f.equipment?.internet?.minBandwidth ?? 0) > 0 ||
          f.equipment?.furniture !== "none" ||
          f.equipment?.sanitary?.wcWomen ||
          f.equipment?.sanitary?.wcMen ||
          f.equipment?.sanitary?.wcAccessible ||
          f.equipment?.other?.smokingArea ||
          f.equipment?.other?.artistDressingRooms ||
          f.equipment?.other?.noNoiseRestrictions ||
          f.equipment?.other?.shower ||
          f.equipment?.other?.backstageArea ||
          f.equipment?.other?.danceFloor)
      ) {
        return true;
      }
      return false;
    });
  }, [value.filters, value.type, value.guests]);

  // Track which accordions are currently open
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);

  // Initialize accordions when dialog opens
  useEffect(() => {
    if (isDialogOpenEffective) {
      // When dialog opens, open accordions with active filters
      const initial = requiredOpenAccordions.includes(LOCATION_SEARCH_ACCORDION)
        ? requiredOpenAccordions
        : [...requiredOpenAccordions];
      const withExpanded =
        expandAccordionOnOpen && !initial.includes(expandAccordionOnOpen)
          ? [...initial, expandAccordionOnOpen]
          : initial;
      setOpenAccordions(withExpanded);
      // Reset manually closed list when dialog opens
      setManuallyClosedAccordions([]);
    }
  }, [isDialogOpenEffective, expandAccordionOnOpen]); // Only run when dialog opens/closes

  const rentalDays: { id: RentalDay; label: string }[] = [
    { id: "monday", label: "Mo" },
    { id: "tuesday", label: "Di" },
    { id: "wednesday", label: "Mi" },
    { id: "thursday", label: "Do" },
    { id: "friday", label: "Fr" },
    { id: "saturday", label: "Sa" },
    { id: "sunday", label: "So" },
  ];
  const hourOptions = Array.from({ length: 24 }).map((_, hour) => ({
    value: `${hour}`,
    label: `${hour.toString().padStart(2, "0")}:00`,
  }));

  const currentYear = getYear(new Date());

  return (
    <Dialog open={isDialogOpenEffective} onOpenChange={setDialogOpen}>
      {trigger}
      <DialogContent>
        <DialogHeader className="sr-only">
          <DialogTitle>Filter</DialogTitle>
          <DialogDescription>
            Setze weitere Filter für die Suche
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="allFilters" variant="dialog-header">
          <TabsList>
            <TabsTrigger value="allFilters">Alle Filter</TabsTrigger>
            {/* <TabsTrigger value="aiSearch" disabled>
              KI-Suche <Badge size={"sm"}>Coming soon</Badge>
            </TabsTrigger> */}
          </TabsList>
          <TabsContent value="allFilters">
            <TabsScrollArea>
              <Accordion
                type="multiple"
                className="w-full"
                value={openAccordions}
                onValueChange={(newValue) => {
                  // Update open accordions
                  setOpenAccordions(newValue);

                  // Track which required accordions were manually closed
                  const closedRequired = requiredOpenAccordions.filter(
                    (item) => !newValue.includes(item),
                  );
                  if (closedRequired.length > 0) {
                    setManuallyClosedAccordions((prev) => [
                      ...new Set([...prev, ...closedRequired]),
                    ]);
                  }
                }}
              >
                <AccordionItem value="filter-location-search">
                  <AccordionTrigger>Standortsuche anpassen</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-4">
                    {value.location && value.location.length > 0 && (
                      <div className="flex flex-wrap items-center gap-6">
                        {value.location.map((loc, index) => (
                          <Badge
                            key={index}
                            onClose={() => {
                              const next = removeLocationSelectionAt(
                                value.location,
                                value.placeIds,
                                index,
                              );
                              onValueChange({
                                ...value,
                                location: next.location,
                                placeIds: next.placeIds,
                              });
                            }}
                          >
                            {loc}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <LocationSearchInput
                      placeholder="Weiteren Ort eingeben"
                      source="filter_dialog"
                      onChange={(
                        details: PlaceDetails | null,
                        meta: { placeId: string | null },
                      ) => {
                        if (!details) return;
                        // Format location string: only city and country
                        const locationParts = [
                          details.city,
                          details.country,
                        ].filter(Boolean);
                        const locationString = locationParts.join(", ");
                        if (!locationString) return;

                        // Add the label and its Place ID together so search can
                        // use (cached) Place Details instead of Geocoding.
                        const next = addLocationSelection(
                          value.location,
                          value.placeIds,
                          locationString,
                          meta.placeId ?? "",
                        );
                        onValueChange({
                          ...value,
                          location: next.location,
                          placeIds: next.placeIds,
                        });
                      }}
                    />
                    <div className="flex flex-col gap-2">
                      <Label className="text-base">Umkreis</Label>
                      <Select
                        value={
                          value.searchRadius !== undefined
                            ? value.searchRadius.toString()
                            : "20"
                        }
                        onValueChange={(val) => {
                          onValueChange({
                            ...value,
                            searchRadius: parseInt(val),
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Umkreis wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0 km</SelectItem>
                          <SelectItem value="20">20 km</SelectItem>
                          <SelectItem value="30">30 km</SelectItem>
                          <SelectItem value="50">50 km</SelectItem>
                          <SelectItem value="80">80 km</SelectItem>
                          <SelectItem value="100">100 km</SelectItem>
                          <SelectItem value="200">200 km</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-2">
                      <Input
                        placeholder="Loc. ID oder Name"
                        iconStart={<PinIcon />}
                        value={value.locationIdOrName ?? ""}
                        onChange={(e) => {
                          onValueChange({
                            ...value,
                            locationIdOrName:
                              e.target.value === ""
                                ? undefined
                                : e.target.value,
                          });
                        }}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-occasion-and-location-type">
                  <AccordionTrigger>Anlass & Locationart</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        let type = value.type;
                        let occasion = value.filters.occasion;
                        let locationType = value.filters.locationType;

                        if (val.categoryId === "occasion") {
                          // Create new array instead of mutating

                          if (val.value) {
                            occasion = [...occasion, val.itemId];
                          } else {
                            occasion = occasion.filter(
                              (id) => id !== val.itemId,
                            );

                            if (val.itemId === type) {
                              type = null;
                            }
                          }
                        } else if (val.categoryId === "location-type") {
                          // Create new array instead of mutating
                          locationType = val.value
                            ? [...locationType, val.itemId]
                            : locationType.filter((id) => id !== val.itemId);
                        }

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            occasion,
                            locationType,
                          },
                          type,
                        });
                      }}
                      categories={[
                        {
                          id: "occasion",
                          label: "Anlass",
                          items: eventTypes.map((eventType) => {
                            return {
                              id: eventType.id,
                              label: eventType.name,
                              amount: eventType.locationCount,
                              selected:
                                value.filters.occasion.includes(eventType.id) ||
                                value.type === eventType.id,
                            };
                          }),
                        },
                        {
                          id: "location-type",
                          label: "Locationart",
                          items: locationTypes.map((locationType) => {
                            return {
                              id: locationType.id,
                              label: locationType.name,
                              amount: locationType.locationCount,
                              selected: value.filters.locationType.includes(
                                locationType.id,
                              ),
                            };
                          }),
                        },
                      ]}
                    />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-guests-and-seating">
                  <AccordionTrigger>Personen & Bestuhlung</AccordionTrigger>
                  <AccordionContent>
                    <h2 className="font-semibold text-lg mb-3">
                      Personen gesamt
                    </h2>

                    <Select
                      value={value.guests || "any"}
                      name="guests"
                      onValueChange={(e) =>
                        onValueChange({ ...value, guests: e as NumberOfGuests })
                      }
                    >
                      <SelectTrigger className="w-[210px]">
                        <SelectValue placeholder="Gästezahl" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">beliebig</SelectItem>
                        {Object.entries(NumberOfGuestsOptions)
                          .filter(([k]) => k !== "any")
                          .map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>

                    <h2 className="font-semibold text-lg mb-3 mt-6">
                      Bestuhlung
                    </h2>
                    <SeatingSelect
                      value={value.filters.seating as SeatingSelectValue}
                      onValueChange={(e) => {
                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            seating: e as SeatingSelectValue,
                          },
                        });
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-location-properties">
                  <AccordionTrigger>
                    Eigenschaften der Location
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        const idToName = {
                          1: "indoor",
                          2: "mixed",
                          3: "outdoor",
                        };

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            locationSettings: {
                              ...value.filters.locationSettings,
                              [idToName[val.itemId as keyof typeof idToName]]:
                                !!val.value,
                            },
                          },
                        });
                      }}
                      categories={[
                        {
                          id: "location-settings",
                          label: "Indoor / Outdoor",
                          items: [
                            {
                              id: 1,
                              label: "Indoor",
                              selected: value.filters.locationSettings.indoor,
                            },
                            {
                              id: 2,
                              label: "Außenfläche(n)",
                              selected: value.filters.locationSettings.mixed,
                            },
                            {
                              id: 3,
                              label: "Outdoor",
                              selected: value.filters.locationSettings.outdoor,
                            },
                          ],
                        },
                      ]}
                    />

                    <div>
                      <h2 className="font-semibold text-lg mb-3">
                        Locationgröße
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex gap-2 flex-col">
                          <Label>Innenfläche gesamt</Label>
                          <NumberInput
                            placeholder="Innenfläche gesamt"
                            suffix="m²"
                            allowEmpty
                            value={
                              value.filters.locationSize.indoorAreaTotal > 0
                                ? value.filters.locationSize.indoorAreaTotal
                                : undefined
                            }
                            onChange={(v) => {
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  locationSize: {
                                    ...value.filters.locationSize,
                                    indoorAreaTotal: v ?? 0,
                                  },
                                },
                              });
                            }}
                          />
                        </div>
                        <div className="flex gap-2 flex-col">
                          <Label>Höchster Raum</Label>

                          <NumberInput
                            placeholder="Max. Höhe"
                            suffix="m"
                            allowEmpty
                            value={
                              value.filters.locationSize.maxHeight > 0
                                ? value.filters.locationSize.maxHeight
                                : undefined
                            }
                            onChange={(v) => {
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  locationSize: {
                                    ...value.filters.locationSize,
                                    maxHeight: v ?? 0,
                                  },
                                },
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* <div>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex gap-2 flex-col">
                          <h2 className="font-semibold text-lg">
                            Eröffnungsjahr
                          </h2>
                          <Select
                            value={
                              value.filters.locationOpening
                                ? `${value.filters.locationOpening}`
                                : undefined
                            }
                            name="locationOpening"
                            onValueChange={(e) =>
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  locationOpening: Number(e),
                                },
                              })
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: currentYear - 1899 }).map((_, i) => {
                                const year = currentYear - i;

                                return (
                                  <SelectItem key={year} value={`${year}`}>
                                    {year}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 flex-col">
                          <h2 className="font-semibold text-lg">
                            Renovierungsjahr
                          </h2>
                          <Select
                            value={
                              value.filters.locationRenovation
                                ? `${value.filters.locationRenovation}`
                                : undefined
                            }
                            name="locationRenovation"
                            onValueChange={(e) =>
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  locationRenovation: Number(e),
                                },
                              })
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: currentYear - 1899 }).map((_, i) => {
                                const year = currentYear - i;

                                return (
                                  <SelectItem key={year} value={`${year}`}>
                                    {year}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div> */}

                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        const idToName = {
                          1: "accessible",
                          2: "dimmable",
                          3: "daylight",
                        };

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            commonProperties: {
                              ...value.filters.commonProperties,
                              [idToName[val.itemId as keyof typeof idToName]]:
                                !!val.value,
                            },
                          },
                        });
                      }}
                      categories={[
                        {
                          id: "common-properties",
                          label: "Allgemeine Eigenschaften",
                          items: [
                            {
                              id: 1,
                              label: "Barrierefrei",
                              selected:
                                value.filters.commonProperties.accessible,
                            },
                            {
                              id: 2,
                              label: "Verdunkelbar",
                              selected: value.filters.commonProperties.dimmable,
                            },
                            {
                              id: 3,
                              label: "Tageslicht",
                              selected: value.filters.commonProperties.daylight,
                            },
                          ],
                        },
                      ]}
                    />

                    <div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                        <div className="flex gap-2 flex-col">
                          <h2 className="font-semibold text-lg">
                            Anzahl Veranstaltungsräume (min.)
                          </h2>
                          <Select
                            key={`numberOfNeededRooms-${value.filters.numberOfNeededRooms ?? "undefined"}`}
                            value={
                              value.filters.numberOfNeededRooms !== undefined &&
                                value.filters.numberOfNeededRooms > 0
                                ? `${value.filters.numberOfNeededRooms}`
                                : undefined
                            }
                            name="numberOfNeededRooms"
                            onValueChange={(e) =>
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  numberOfNeededRooms: Number(e),
                                },
                              })
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 50 }).map((_, i) => (
                                <SelectItem key={i} value={`${i}`}>
                                  {i}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 flex-col">
                          <h2 className="font-semibold text-lg">
                            Außenfläche (min. m²)
                          </h2>
                          <NumberInput
                            placeholder="Außenfläche"
                            suffix="m²"
                            allowEmpty
                            value={
                              value.filters.outerAreaSize > 0
                                ? value.filters.outerAreaSize
                                : undefined
                            }
                            onChange={(v) => {
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  outerAreaSize: v ?? 0,
                                },
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* <div>
                      <h2 className="font-semibold text-lg mb-3">Bühne</h2>
                      <Switch
                        checked={value.filters.stage}
                        onCheckedChange={(e) => {
                          onValueChange({
                            ...value,
                            filters: {
                              ...value.filters,
                              stage: e,
                            },
                          });
                        }}
                      />
                    </div> */}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-location-and-infrastructure">
                  <AccordionTrigger>Lage</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        let infrastructure = value.filters.infrastructure ?? [];

                        if (val.categoryId === "infrastructure") {
                          infrastructure = val.value
                            ? [...infrastructure, val.itemId]
                            : infrastructure.filter((id) => id !== val.itemId);
                        }

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            infrastructure,
                          },
                        });
                      }}
                      categories={[
                        {
                          id: "infrastructure",
                          label: "Lage",
                          items: (infraTypes ?? []).map((infraType) => {
                            return {
                              id: infraType.id,
                              label: infraType.name,
                              amount: infraType.locationCount,
                              selected: (
                                value.filters.infrastructure ?? []
                              ).includes(infraType.id),
                            };
                          }),
                        },
                      ]}
                    />

                    {/* <div>
                      <h2 className="font-semibold text-lg mb-3">
                        Entfernungen
                      </h2>
                      <div className="flex flex-col gap-3">
                        {(value.filters.distances ?? []).map(
                          (distance, index) => {
                            const currentValue = value;
                            return (
                              <div key={index} className="flex gap-2 items-end">
                                <div className="flex-1">
                                  <Select
                                    value={distance.type}
                                    onValueChange={(selectedValue: string) => {
                                      const typedValue = selectedValue as
                                        | "motorway"
                                        | "railstation"
                                        | "airport"
                                        | "city-center"
                                        | "public-transport";
                                      const newDistances = [
                                        ...(currentValue.filters.distances ??
                                          []),
                                      ];
                                      newDistances[index] = {
                                        ...newDistances[index],
                                        type: typedValue,
                                      };
                                      onValueChange({
                                        ...currentValue,
                                        filters: {
                                          ...currentValue.filters,
                                          distances: newDistances,
                                        },
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="motorway">
                                        <img
                                          src={iconMotorway}
                                          alt=""
                                          className="inline-block mr-2"
                                        />
                                        Autobahn
                                      </SelectItem>
                                      <SelectItem value="railstation">
                                        <img
                                          src={iconRailstation}
                                          alt=""
                                          className="inline-block mr-2"
                                        />
                                        Bahnhof
                                      </SelectItem>
                                      <SelectItem value="airport">
                                        <img
                                          src={iconAirport}
                                          alt=""
                                          className="inline-block mr-2"
                                        />
                                        Flughafen
                                      </SelectItem>
                                      <SelectItem value="city-center">
                                        <img
                                          src={iconCityCenter}
                                          alt=""
                                          className="inline-block mr-2"
                                        />
                                        Stadtzentrum
                                      </SelectItem>
                                      <SelectItem value="public-transport">
                                        <img
                                          src={iconPublicTransport}
                                          alt=""
                                          className="inline-block mr-2"
                                        />
                                        ÖPNV
                                      </SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="w-[150px]">
                                  <Input
                                    type="number"
                                    placeholder="Max. Entfernung"
                                    prefix="max.km"
                                    value={`${distance.maxDistance}`}
                                    onChange={(e) => {
                                      const newDistances = [
                                        ...(currentValue.filters.distances ??
                                          []),
                                      ];
                                      newDistances[index] = {
                                        ...newDistances[index],
                                        maxDistance: Number(e.target.value),
                                      };
                                      onValueChange({
                                        ...currentValue,
                                        filters: {
                                          ...currentValue.filters,
                                          distances: newDistances,
                                        },
                                      });
                                    }}
                                  />
                                </div>
                                <Button
                                  variant="flatDark"
                                  size="icon"
                                  onClick={() => {
                                    const newDistances = (
                                      currentValue.filters.distances ?? []
                                    ).filter((_, i) => i !== index);
                                    onValueChange({
                                      ...currentValue,
                                      filters: {
                                        ...currentValue.filters,
                                        distances: newDistances,
                                      },
                                    });
                                  }}
                                >
                                  <TrashIcon />
                                </Button>
                              </div>
                            );
                          }
                        )}
                        <Button
                          variant="ghost"
                          className="text-base font-normal text-neutral-dark w-auto self-start shrink-0"
                          onClick={() => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                distances: [
                                  ...(value.filters.distances ?? []),
                                  {
                                    type: "motorway",
                                    maxDistance: 0,
                                  },
                                ],
                              },
                            });
                          }}
                        >
                          Entfernung hinzufügen
                          <PinPlusIcon className="size-5" />
                        </Button>
                      </div>
                    </div> */}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-budget">
                  <AccordionTrigger>Budget / Preise</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <div>
                      <Select
                        key={`budgetType-${value.filters.budget?.type ?? "undefined"}`}
                        value={
                          value.filters.budget?.type
                            ? value.filters.budget.type
                            : "none"
                        }
                        name="budgetType"
                        onValueChange={(e) => {
                          if (e === "none") {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                budget: undefined,
                              },
                            });
                            return;
                          }
                          onValueChange({
                            ...value,
                            filters: {
                              ...value.filters,
                              budget: {
                                type: e as
                                  | "rateDaily"
                                  | "rateHourly"
                                  | "minBudgetTotal"
                                  | "minBudgetPerPerson",
                                currency:
                                  value.filters.budget?.currency || "EUR",
                                from: value.filters.budget?.from ?? null,
                                to: value.filters.budget?.to ?? null,
                              },
                            },
                          });
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Preis-Typ wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Beliebig</SelectItem>
                          <SelectItem value="rateDaily">Tagesmiete</SelectItem>
                          <SelectItem value="rateHourly">
                            Miete pro Stunde
                          </SelectItem>
                          <SelectItem value="minBudgetTotal">
                            Mindestverzehr
                          </SelectItem>
                          <SelectItem value="minBudgetPerPerson">
                            Pauschale pro Person
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      {/* <div className="grid grid-cols-2 gap-6"> */}
                      {/* <div className="flex gap-2 flex-col">
                          <Label>Ab</Label>
                          <div className="flex gap-2">
                            <NumberInput
                              placeholder="Von"
                              allowEmpty
                              value={
                                value.filters.budget &&
                                  value.filters.budget.from !== null &&
                                  value.filters.budget.from > 0
                                  ? value.filters.budget.from / 100
                                  : undefined
                              }
                              onChange={(v) => {
                                const fromValue = v !== undefined && v !== 0 ? v * 100 : null;
                                onValueChange({
                                  ...value,
                                  filters: {
                                    ...value.filters,
                                    budget: {
                                      type: value.filters.budget?.type || "rateDaily",
                                      currency: value.filters.budget?.currency || "EUR",
                                      from: fromValue,
                                      to: value.filters.budget?.to ?? null,
                                    },
                                  },
                                });
                              }}
                              onBlur={(v) => {
                                const fromValue = v !== undefined ? v * 100 : null;
                                const currentTo = value.filters.budget?.to ?? null;

                                // Ensure "Von" is not greater than "Bis"
                                let finalFrom = fromValue;
                                if (fromValue !== null && currentTo !== null && fromValue > currentTo) {
                                  finalFrom = currentTo;
                                }

                                onValueChange({
                                  ...value,
                                  filters: {
                                    ...value.filters,
                                    budget: {
                                      type: value.filters.budget?.type || "rateDaily",
                                      currency: value.filters.budget?.currency || "EUR",
                                      from: finalFrom,
                                      to: currentTo,
                                    },
                                  },
                                });
                              }}
                              className="flex-1"
                            />
                            <Select
                              value={value.filters.budget?.currency || "EUR"}
                              name="budgetCurrency"
                              onValueChange={(e) =>
                                onValueChange({
                                  ...value,
                                  filters: {
                                    ...value.filters,
                                    budget: {
                                      type:
                                        value.filters.budget?.type || "rateDaily",
                                      currency: e as "EUR" | "CHF" | "USD",
                                      from: value.filters.budget?.from ?? null,
                                      to: value.filters.budget?.to ?? null,
                                    },
                                  },
                                })
                              }
                            >
                              <SelectTrigger className="w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="EUR">EUR</SelectItem>
                                <SelectItem value="CHF">CHF</SelectItem>
                                <SelectItem value="USD">USD</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div> */}
                      <div className="flex gap-2 flex-col">
                        <Label>Max. Preis</Label>
                        <div className="flex gap-2">
                          <NumberInput
                            placeholder="Preisobergrenze"
                            allowEmpty
                            value={
                              value.filters.budget &&
                                value.filters.budget.to !== null &&
                                value.filters.budget.to > 0
                                ? value.filters.budget.to / 100
                                : undefined
                            }
                            onChange={(v) => {
                              const toValue =
                                v !== undefined && v !== 0 ? v * 100 : null;
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  budget: {
                                    type:
                                      value.filters.budget?.type || "rateDaily",
                                    currency:
                                      value.filters.budget?.currency || "EUR",
                                    from: value.filters.budget?.from ?? null,
                                    to: toValue,
                                  },
                                },
                              });
                            }}
                            onBlur={(v) => {
                              const toValue = v !== undefined ? v * 100 : null;
                              const currentFrom =
                                value.filters.budget?.from ?? null;

                              // Ensure "Bis" is not smaller than "Von"
                              let finalTo = toValue;
                              if (
                                toValue !== null &&
                                currentFrom !== null &&
                                toValue < currentFrom
                              ) {
                                finalTo = currentFrom;
                              }

                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  budget: {
                                    type:
                                      value.filters.budget?.type || "rateDaily",
                                    currency:
                                      value.filters.budget?.currency || "EUR",
                                    from: currentFrom,
                                    to: finalTo,
                                  },
                                },
                              });
                            }}
                            className="flex-1"
                          />
                          <Select
                            value={value.filters.budget?.currency || "EUR"}
                            name="budgetCurrency"
                            onValueChange={(e) =>
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  budget: {
                                    type:
                                      value.filters.budget?.type || "rateDaily",
                                    currency: e as "EUR" | "CHF" | "USD",
                                    from: value.filters.budget?.from ?? null,
                                    to: value.filters.budget?.to ?? null,
                                  },
                                },
                              })
                            }
                          >
                            <SelectTrigger className="w-[100px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="EUR">EUR</SelectItem>
                              {/* <SelectItem value="CHF">CHF</SelectItem>
                              <SelectItem value="USD">USD</SelectItem> */}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {/* </div> */}
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-contract-bindings">
                  <AccordionTrigger>Vertragsbindungen</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <div>
                      <h2 className="font-semibold text-lg mb-3">Catering</h2>
                      <div className="flex gap-2">
                        <Button
                          variant={
                            value.filters.cateringBinding === "none"
                              ? "default"
                              : "outline"
                          }
                          onClick={() => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                cateringBinding:
                                  value.filters.cateringBinding === "none"
                                    ? ""
                                    : "none",
                              },
                            });
                          }}
                        >
                          Keine Cateringbindung
                        </Button>
                        {/* <Button
                          variant={
                            value.filters.cateringBinding === "fixed"
                              ? "default"
                              : "outline"
                          }
                          onClick={() => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                cateringBinding:
                                  value.filters.cateringBinding === "fixed"
                                    ? ""
                                    : "fixed",
                              },
                            });
                          }}
                        >
                          Feste Gastronomie
                        </Button> */}
                      </div>
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg mb-3">Technik</h2>
                      <div className="flex gap-2">
                        <Button
                          variant={
                            value.filters.techBinding === "none"
                              ? "default"
                              : "outline"
                          }
                          onClick={() => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                techBinding:
                                  value.filters.techBinding === "none"
                                    ? ""
                                    : "none",
                              },
                            });
                          }}
                        >
                          Keine Technikbindung
                        </Button>
                        {/* <Button
                          variant={
                            value.filters.techBinding === "fixed"
                              ? "default"
                              : "outline"
                          }
                          onClick={() => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                techBinding:
                                  value.filters.techBinding === "fixed"
                                    ? ""
                                    : "fixed",
                              },
                            });
                          }}
                        >
                          Fester Technikpartner
                        </Button> */}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {/* <AccordionItem value="filter-equipment">
                  <AccordionTrigger>Ausstattung</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp128: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.acPower = {
                              ...currentEquipment.acPower,
                              volt110: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.acPower = {
                              ...currentEquipment.acPower,
                              volt230: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "ac-power",
                            label: "Wechselstrom",
                            items: [
                              {
                                id: 1,
                                label: "110 Volt",
                                selected:
                                  value.filters.equipment?.acPower?.volt110 ??
                                  false,
                              },
                              {
                                id: 2,
                                label: "230 Volt",
                                selected:
                                  value.filters.equipment?.acPower?.volt230 ??
                                  false,
                              },
                            ],
                          },
                        ]}
                      />
                    </div>

                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp125: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.threePhasePower = {
                              ...currentEquipment.threePhasePower,
                              amp16: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.threePhasePower = {
                              ...currentEquipment.threePhasePower,
                              amp32: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.threePhasePower = {
                              ...currentEquipment.threePhasePower,
                              amp64: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.threePhasePower = {
                              ...currentEquipment.threePhasePower,
                              amp125: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "three-phase-power",
                            label: "CEE-Drehstrom (Starkstrom)",
                            items: [
                              {
                                id: 1,
                                label: "16A",
                                selected:
                                  value.filters.equipment?.threePhasePower
                                    ?.amp16 ?? false,
                              },
                              {
                                id: 2,
                                label: "32A",
                                selected:
                                  value.filters.equipment?.threePhasePower
                                    ?.amp32 ?? false,
                              },
                              {
                                id: 3,
                                label: "63A",
                                selected:
                                  value.filters.equipment?.threePhasePower
                                    ?.amp64 ?? false,
                              },
                              {
                                id: 4,
                                label: "128A",
                                selected:
                                  value.filters.equipment?.threePhasePower
                                    ?.amp128 ?? false,
                              },
                            ],
                          },
                        ]}
                      />
                    </div>

                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp128: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.technology = {
                              ...currentEquipment.technology,
                              lighting: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.technology = {
                              ...currentEquipment.technology,
                              presentationVideo: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.technology = {
                              ...currentEquipment.technology,
                              audio: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.technology = {
                              ...currentEquipment.technology,
                              heatingClimate: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "technology",
                            label: "Technik",
                            items: [
                              {
                                id: 1,
                                label: "Lichttechnik",
                                selected:
                                  value.filters.equipment?.technology
                                    ?.lighting ?? false,
                              },
                              {
                                id: 2,
                                label: "Präsentations- & Videotechnik",
                                selected:
                                  value.filters.equipment?.technology
                                    ?.presentationVideo ?? false,
                              },
                              {
                                id: 3,
                                label: "Tontechnik",
                                selected:
                                  value.filters.equipment?.technology?.audio ??
                                  false,
                              },
                              {
                                id: 4,
                                label: "Heiz- & Klimatechnik",
                                selected:
                                  value.filters.equipment?.technology
                                    ?.heatingClimate ?? false,
                              },
                            ],
                          },
                        ]}
                      />
                    </div>

                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp125: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.specialTechnology = {
                              ...currentEquipment.specialTechnology,
                              rigging: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.specialTechnology = {
                              ...currentEquipment.specialTechnology,
                              v1: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.specialTechnology = {
                              ...currentEquipment.specialTechnology,
                              v2: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.specialTechnology = {
                              ...currentEquipment.specialTechnology,
                              v3: !!val.value,
                            };
                          } else if (val.itemId === 5) {
                            updated.specialTechnology = {
                              ...currentEquipment.specialTechnology,
                              v4: !!val.value,
                            };
                          } else if (val.itemId === 6) {
                            updated.specialTechnology = {
                              ...currentEquipment.specialTechnology,
                              v5: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "special-technology",
                            label: "Spezielle Technik",
                            items: [
                              {
                                id: 1,
                                label: "Rigging (Traverse)",
                                selected:
                                  value.filters.equipment?.specialTechnology
                                    ?.rigging ?? false,
                              },
                              {
                                id: 2,
                                label: "V1",
                                selected:
                                  value.filters.equipment?.specialTechnology
                                    ?.v1 ?? false,
                              },
                              {
                                id: 3,
                                label: "V2",
                                selected:
                                  value.filters.equipment?.specialTechnology
                                    ?.v2 ?? false,
                              },
                              {
                                id: 4,
                                label: "V3",
                                selected:
                                  value.filters.equipment?.specialTechnology
                                    ?.v3 ?? false,
                              },
                              {
                                id: 5,
                                label: "V4",
                                selected:
                                  value.filters.equipment?.specialTechnology
                                    ?.v4 ?? false,
                              },
                              {
                                id: 6,
                                label: "V5",
                                selected:
                                  value.filters.equipment?.specialTechnology
                                    ?.v5 ?? false,
                              },
                            ],
                          },
                        ]}
                      />
                    </div>

                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp125: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.internet = {
                              ...currentEquipment.internet,
                              g5: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.internet = {
                              ...currentEquipment.internet,
                              g4: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.internet = {
                              ...currentEquipment.internet,
                              g3: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.internet = {
                              ...currentEquipment.internet,
                              g2: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "internet",
                            label: "Internetverfügbarkeit",
                            items: [
                              {
                                id: 1,
                                label: "5G",
                                selected:
                                  value.filters.equipment?.internet?.g5 ??
                                  false,
                              },
                              {
                                id: 2,
                                label: "4G",
                                selected:
                                  value.filters.equipment?.internet?.g4 ??
                                  false,
                              },
                              {
                                id: 3,
                                label: "3G",
                                selected:
                                  value.filters.equipment?.internet?.g3 ??
                                  false,
                              },
                              {
                                id: 4,
                                label: "2G",
                                selected:
                                  value.filters.equipment?.internet?.g2 ??
                                  false,
                              },
                            ],
                          },
                        ]}
                      />
                      <div className="mt-4">
                        <Label htmlFor="min-bandwidth">Bandbreite (min.)</Label>
                        <NumberInput
                          id="min-bandwidth"
                          placeholder="Bandbreite"
                          suffix="Mbit/s"
                          value={value.filters.equipment?.internet?.minBandwidth ?? 0}
                          onChange={(e) => {
                            const currentEquipment = value.filters
                              .equipment ?? {
                              acPower: { volt110: false, volt230: false },
                              threePhasePower: {
                                amp16: false,
                                amp32: false,
                                amp64: false,
                                amp125: false,
                              },
                              technology: {
                                lighting: false,
                                presentationVideo: false,
                                audio: false,
                                heatingClimate: false,
                              },
                              specialTechnology: {
                                rigging: false,
                                v1: false,
                                v2: false,
                                v3: false,
                                v4: false,
                                v5: false,
                              },
                              internet: {
                                g5: false,
                                g4: false,
                                g3: false,
                                g2: false,
                                minBandwidth: 0,
                              },
                              furniture: "none",
                              sanitary: {
                                wcWomen: false,
                                wcMen: false,
                                wcAccessible: false,
                              },
                              other: {
                                smokingArea: false,
                                artistDressingRooms: false,
                                noNoiseRestrictions: false,
                                shower: false,
                                backstageArea: false,
                                danceFloor: false,
                              },
                            };
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                equipment: {
                                  ...currentEquipment,
                                  internet: {
                                    ...currentEquipment.internet,
                                    minBandwidth: e ?? 0,
                                  },
                                },
                              },
                            });
                          }}
                          className="mt-3 w-[200px]"
                        />
                      </div>
                    </div>

                    <div>
                      <h2 className="font-semibold text-lg mb-3">Mobiliar</h2>
                      <RadioGroup
                        value={value.filters.equipment?.furniture ?? "none"}
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp125: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          onValueChange({
                            ...value,
                            filters: {
                              ...value.filters,
                              equipment: {
                                ...currentEquipment,
                                furniture: val as
                                  | "available"
                                  | "bookable"
                                  | "none",
                              },
                            },
                          });
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem
                            value="available"
                            id="furniture-available"
                          />
                          <Label htmlFor="furniture-available">Vorhanden</Label>
                        </div>
                        <div className="flex items-center gap-3">
                          <RadioGroupItem
                            value="bookable"
                            id="furniture-bookable"
                          />
                          <Label htmlFor="furniture-bookable">Buchbar</Label>
                        </div>
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="none" id="furniture-none" />
                          <Label htmlFor="furniture-none">Kein</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp125: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.sanitary = {
                              ...currentEquipment.sanitary,
                              wcWomen: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.sanitary = {
                              ...currentEquipment.sanitary,
                              wcMen: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.sanitary = {
                              ...currentEquipment.sanitary,
                              wcAccessible: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "sanitary",
                            label: "Sanitäre Einrichtungen",
                            items: [
                              {
                                id: 1,
                                label: "WC Damen",
                                selected:
                                  value.filters.equipment?.sanitary?.wcWomen ??
                                  false,
                              },
                              {
                                id: 2,
                                label: "WC Herren",
                                selected:
                                  value.filters.equipment?.sanitary?.wcMen ??
                                  false,
                              },
                              {
                                id: 3,
                                label: "WC barrierefrei",
                                selected:
                                  value.filters.equipment?.sanitary
                                    ?.wcAccessible ?? false,
                              },
                            ],
                          },
                        ]}
                      />
                    </div>

                    <div>
                      <CheckboxFilterOptions
                        onValueChange={(val) => {
                          const currentEquipment = value.filters.equipment ?? {
                            acPower: { volt110: false, volt230: false },
                            threePhasePower: {
                              amp16: false,
                              amp32: false,
                              amp64: false,
                              amp125: false,
                            },
                            technology: {
                              lighting: false,
                              presentationVideo: false,
                              audio: false,
                              heatingClimate: false,
                            },
                            specialTechnology: {
                              rigging: false,
                              v1: false,
                              v2: false,
                              v3: false,
                              v4: false,
                              v5: false,
                            },
                            internet: {
                              g5: false,
                              g4: false,
                              g3: false,
                              g2: false,
                              minBandwidth: 0,
                            },
                            furniture: "none",
                            sanitary: {
                              wcWomen: false,
                              wcMen: false,
                              wcAccessible: false,
                            },
                            other: {
                              smokingArea: false,
                              artistDressingRooms: false,
                              noNoiseRestrictions: false,
                              shower: false,
                              backstageArea: false,
                              danceFloor: false,
                            },
                          };
                          const updated = { ...currentEquipment };
                          if (val.itemId === 1) {
                            updated.other = {
                              ...currentEquipment.other,
                              smokingArea: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.other = {
                              ...currentEquipment.other,
                              artistDressingRooms: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.other = {
                              ...currentEquipment.other,
                              noNoiseRestrictions: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.other = {
                              ...currentEquipment.other,
                              shower: !!val.value,
                            };
                          } else if (val.itemId === 5) {
                            updated.other = {
                              ...currentEquipment.other,
                              backstageArea: !!val.value,
                            };
                          } else if (val.itemId === 6) {
                            updated.other = {
                              ...currentEquipment.other,
                              danceFloor: !!val.value,
                            };
                          }
                          onValueChange({
                            ...value,
                            filters: { ...value.filters, equipment: updated },
                          });
                        }}
                        categories={[
                          {
                            id: "other",
                            label: "Sonstiges",
                            items: [
                              {
                                id: 1,
                                label: "Raucher-Bereich",
                                selected:
                                  value.filters.equipment?.other?.smokingArea ??
                                  false,
                              },
                              {
                                id: 2,
                                label: "Künstlergarderoben",
                                selected:
                                  value.filters.equipment?.other
                                    ?.artistDressingRooms ?? false,
                              },
                              {
                                id: 3,
                                label: "keine Lärmschutzauflagen",
                                selected:
                                  value.filters.equipment?.other
                                    ?.noNoiseRestrictions ?? false,
                              },
                              {
                                id: 4,
                                label: "Dusche",
                                selected:
                                  value.filters.equipment?.other?.shower ??
                                  false,
                              },
                              {
                                id: 5,
                                label: "Backstage-Bereich",
                                selected:
                                  value.filters.equipment?.other
                                    ?.backstageArea ?? false,
                              },
                              {
                                id: 6,
                                label: "Tanzfläche",
                                selected:
                                  value.filters.equipment?.other?.danceFloor ??
                                  false,
                              },
                            ],
                          },
                        ]}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem> */}
                {/* <AccordionItem value="filter-delivery-logistics">
                  <AccordionTrigger>Anlieferung / Logistik</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        const currentDeliveryLogistics = value.filters
                          .deliveryLogistics ?? {
                          truckAccess: false,
                          carAccess: false,
                          freightElevator: false,
                          loadingRamp: false,
                        };

                        const updatedDeliveryLogistics = {
                          ...currentDeliveryLogistics,
                        };

                        if (val.itemId === 1) {
                          updatedDeliveryLogistics.carAccess = !!val.value;
                        } else if (val.itemId === 2) {
                          updatedDeliveryLogistics.truckAccess = !!val.value;
                        } else if (val.itemId === 3) {
                          updatedDeliveryLogistics.freightElevator =
                            !!val.value;
                        } else if (val.itemId === 4) {
                          updatedDeliveryLogistics.loadingRamp = !!val.value;
                        }

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            deliveryLogistics: updatedDeliveryLogistics,
                          },
                        });
                      }}
                      categories={[
                        {
                          id: "delivery-logistics",
                          label: "Anlieferung / Logistik",
                          items: [
                            {
                              id: 1,
                              label: "PKW (Location innen)",
                              selected:
                                value.filters.deliveryLogistics?.carAccess ??
                                false,
                            },
                            {
                              id: 2,
                              label: "LKW (Location innen)",
                              selected:
                                value.filters.deliveryLogistics?.truckAccess ??
                                false,
                            },
                            // {
                            //   id: 3,
                            //   label: "Lastenaufzug",
                            //   selected:
                            //     value.filters.deliveryLogistics
                            //       ?.freightElevator ?? false,
                            // },
                            // {
                            //   id: 4,
                            //   label: "Laderampe",
                            //   selected:
                            //     value.filters.deliveryLogistics?.loadingRamp ??
                            //     false,
                            // },
                          ],
                        },
                      ]}
                    />
                  </AccordionContent>
                </AccordionItem> */}
                <AccordionItem value="filter-parking-spaces">
                  <AccordionTrigger>Parken</AccordionTrigger>
                  <AccordionContent>
                    <div>
                      <Label className="text-lg font-semibold mb-2 block">
                        Parkplätze
                      </Label>
                      <RadioGroup
                        value={value.filters.parkingLots ?? "none"}
                        onValueChange={(val) => {
                          onValueChange({
                            ...value,
                            filters: {
                              ...value.filters,
                              parkingLots:
                                val === "none"
                                  ? undefined
                                  : (val as "yes" | "noOwn"),
                            },
                          });
                        }}
                        className="mt-2"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="none" id="parking-none" />
                          <Label
                            htmlFor="parking-none"
                            className="font-normal cursor-pointer"
                          >
                            Keine Auswahl
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="parking-own" />
                          <Label
                            htmlFor="parking-own"
                            className="font-normal cursor-pointer"
                          >
                            Eigene Parkplätze
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="noOwn" id="parking-nearby" />
                          <Label
                            htmlFor="parking-nearby"
                            className="font-normal cursor-pointer"
                          >
                            Fremde Parkplätze in unmittelbarer Nähe
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                    {/* <Label
                      className="text-base"
                      htmlFor="numberOfParkingSpaces"
                    >
                      Mindestens verfügbare Parkplätze
                    </Label>
                    <Input
                      type="number"
                      id="numberOfParkingSpaces"
                      numberControls
                      value={value.filters.numberOfParkingSpaces}
                      onChange={(e) => {
                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            numberOfParkingSpaces: Number(e.target.value),
                          },
                        });
                      }}
                      onIncrease={() => {
                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            numberOfParkingSpaces:
                              value.filters.numberOfParkingSpaces + 1,
                          },
                        });
                      }}
                      onDecrease={() => {
                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            numberOfParkingSpaces:
                              value.filters.numberOfParkingSpaces - 1,
                          },
                        });
                      }}
                      className="mt-3 w-[200px]"
                    /> */}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-overnight-options">
                  <AccordionTrigger>
                    Übernachtungsmöglichkeiten
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="mb-4">
                      <Label className="text-base">
                        Soll die Location über Übernachtungsmöglichkeiten
                        verfügen?
                      </Label>
                      <div className="flex gap-2 items-center mt-2">
                        <Switch
                          checked={value.filters.hasAccommodations ?? false}
                          onCheckedChange={(checked) => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                hasAccommodations: checked,
                              },
                            });
                          }}
                        />
                        {value.filters.hasAccommodations ? (
                          <span>Ja</span>
                        ) : (
                          <span>Nein</span>
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <Label
                        className="text-lg font-semibold"
                        htmlFor="hotelRooms"
                      >
                        Anzahl der Zimmer
                      </Label>
                      <NumberInput
                        id="hotelRooms"
                        numberControls
                        value={value.filters.hotelRooms}
                        onChange={(v) => {
                          onValueChange({
                            ...value,
                            filters: {
                              ...value.filters,
                              hotelRooms: v ?? 0,
                            },
                          });
                        }}
                        className="mt-3 w-[200px]"
                      />
                    </div>

                    <div className="mb-4">
                      <Label className="text-lg font-semibold">
                        Hotelklassifizierung
                      </Label>
                      <div className="mt-2 flex gap-2 items-center">
                        <IconSelect
                          className="grid grid-cols-3"
                          value={value.filters.hotelStars.toString()}
                          onValueChange={(newValue) => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                hotelStars: Number(newValue),
                              },
                            });
                          }}
                          items={[
                            {
                              id: "0",
                              label: "Keine",
                              icon: <NoAccessIcon />,
                            },
                            {
                              id: "1",
                              label: "1",
                              icon: <OneStarIcon />,
                            },
                            {
                              id: "2",
                              label: "2",
                              icon: <TwoStarsIcon />,
                            },
                            {
                              id: "3",
                              label: "3",
                              icon: <ThreeStarsIcon />,
                            },
                            {
                              id: "4",
                              label: "4",
                              icon: <FourStarsIcon />,
                            },
                            {
                              id: "5",
                              label: "5",
                              icon: <FiveStarsIcon />,
                            },
                          ]}
                        />
                      </div>
                    </div>

                    {/* <div>
                      <Label className="text-lg font-semibold mb-3 block">
                        Unterkunftsart
                      </Label>
                      <div className="grid grid-cols-2 gap-5">
                        {hotelTypes?.map((hotelType) => (
                          <div className="flex gap-2" key={hotelType.id}>
                            <Checkbox
                              id={`hotel-type-${hotelType.id}`}
                              checked={value.filters.hotelTypes?.includes(
                                hotelType.id
                              )}
                              onCheckedChange={(checked) => {
                                const currentTypes =
                                  value.filters.hotelTypes ?? [];
                                if (checked) {
                                  onValueChange({
                                    ...value,
                                    filters: {
                                      ...value.filters,
                                      hotelTypes: [...currentTypes, hotelType.id],
                                    },
                                  });
                                } else {
                                  onValueChange({
                                    ...value,
                                    filters: {
                                      ...value.filters,
                                      hotelTypes: currentTypes.filter(
                                        (id) => id !== hotelType.id
                                      ),
                                    },
                                  });
                                }
                              }}
                            />
                            <Label htmlFor={`hotel-type-${hotelType.id}`}>
                              {hotelType.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div> */}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="filter-rental-duration">
                  <AccordionTrigger>Mietdauer</AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-5 text-balance">
                    <div className="flex flex-wrap gap-2">
                      {rentalDays.map((day) => {
                        const selected =
                          value.filters.rentalDuration.days.includes(day.id);
                        return (
                          <Button
                            key={day.id}
                            variant={selected ? "default" : "outline"}
                            onClick={() => {
                              const days = selected
                                ? value.filters.rentalDuration.days.filter(
                                  (d) => d !== day.id,
                                )
                                : [
                                  ...value.filters.rentalDuration.days,
                                  day.id,
                                ];
                              onValueChange({
                                ...value,
                                filters: {
                                  ...value.filters,
                                  rentalDuration: {
                                    ...value.filters.rentalDuration,
                                    days,
                                  },
                                },
                              });
                            }}
                          >
                            {day.label}
                          </Button>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-4 max-w-[360px]">
                      <div className="flex flex-col gap-2">
                        <Label>von</Label>
                        <Select
                          value={
                            value.filters.rentalDuration.from !== null
                              ? `${value.filters.rentalDuration.from}`
                              : ""
                          }
                          onValueChange={(selectedHour) => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                rentalDuration: {
                                  ...value.filters.rentalDuration,
                                  from: selectedHour
                                    ? Number(selectedHour)
                                    : null,
                                },
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Uhrzeit wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {hourOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label>bis</Label>
                        <Select
                          value={
                            value.filters.rentalDuration.to !== null
                              ? `${value.filters.rentalDuration.to}`
                              : ""
                          }
                          onValueChange={(selectedHour) => {
                            onValueChange({
                              ...value,
                              filters: {
                                ...value.filters,
                                rentalDuration: {
                                  ...value.filters.rentalDuration,
                                  to: selectedHour
                                    ? Number(selectedHour)
                                    : null,
                                },
                              },
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Uhrzeit wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {hourOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                {/* <AccordionItem value="filter-leisure-sport-wellness">
                  <AccordionTrigger>
                    Freizeit / Sport / Wellness
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        const currentLeisureSportWellness = value.filters
                          .leisureSportWellness ?? {
                          leisure: { sightseeing: false },
                          sport: {
                            golf: false,
                            tennis: false,
                            sailing: false,
                            ski: false,
                          },
                          wellness: {
                            sauna: false,
                            massage: false,
                            indoorPool: false,
                            outdoorPool: false,
                          },
                        };

                        const updated = { ...currentLeisureSportWellness };

                        if (val.categoryId === "leisure") {
                          updated.leisure = {
                            ...currentLeisureSportWellness.leisure,
                            sightseeing: !!val.value,
                          };
                        } else if (val.categoryId === "sport") {
                          if (val.itemId === 1) {
                            updated.sport = {
                              ...currentLeisureSportWellness.sport,
                              golf: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.sport = {
                              ...currentLeisureSportWellness.sport,
                              tennis: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.sport = {
                              ...currentLeisureSportWellness.sport,
                              sailing: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.sport = {
                              ...currentLeisureSportWellness.sport,
                              ski: !!val.value,
                            };
                          }
                        } else if (val.categoryId === "wellness") {
                          if (val.itemId === 1) {
                            updated.wellness = {
                              ...currentLeisureSportWellness.wellness,
                              sauna: !!val.value,
                            };
                          } else if (val.itemId === 2) {
                            updated.wellness = {
                              ...currentLeisureSportWellness.wellness,
                              massage: !!val.value,
                            };
                          } else if (val.itemId === 3) {
                            updated.wellness = {
                              ...currentLeisureSportWellness.wellness,
                              indoorPool: !!val.value,
                            };
                          } else if (val.itemId === 4) {
                            updated.wellness = {
                              ...currentLeisureSportWellness.wellness,
                              outdoorPool: !!val.value,
                            };
                          }
                        }

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            leisureSportWellness: updated,
                          },
                        });
                      }}
                      categories={[
                        {
                          id: "leisure",
                          label: "Freizeit",
                          items: [
                            {
                              id: 1,
                              label: "Sightseeing",
                              selected:
                                value.filters.leisureSportWellness?.leisure
                                  ?.sightseeing ?? false,
                            },
                          ],
                        },
                        {
                          id: "sport",
                          label: "Sport",
                          items: [
                            {
                              id: 1,
                              label: "Golf",
                              selected:
                                value.filters.leisureSportWellness?.sport
                                  ?.golf ?? false,
                            },
                            {
                              id: 2,
                              label: "Tennis",
                              selected:
                                value.filters.leisureSportWellness?.sport
                                  ?.tennis ?? false,
                            },
                            {
                              id: 3,
                              label: "Segeln",
                              selected:
                                value.filters.leisureSportWellness?.sport
                                  ?.sailing ?? false,
                            },
                            {
                              id: 4,
                              label: "Ski",
                              selected:
                                value.filters.leisureSportWellness?.sport
                                  ?.ski ?? false,
                            },
                          ],
                        },
                        {
                          id: "wellness",
                          label: "Wellness",
                          items: [
                            {
                              id: 1,
                              label: "Sauna",
                              selected:
                                value.filters.leisureSportWellness?.wellness
                                  ?.sauna ?? false,
                            },
                            {
                              id: 2,
                              label: "Massage",
                              selected:
                                value.filters.leisureSportWellness?.wellness
                                  ?.massage ?? false,
                            },
                            {
                              id: 3,
                              label: "Schwimmbad indoor",
                              selected:
                                value.filters.leisureSportWellness?.wellness
                                  ?.indoorPool ?? false,
                            },
                            {
                              id: 4,
                              label: "Schwimmbad outdoor",
                              selected:
                                value.filters.leisureSportWellness?.wellness
                                  ?.outdoorPool ?? false,
                            },
                          ],
                        },
                      ]}
                    />
                  </AccordionContent>
                </AccordionItem> */}
                {/* <AccordionItem value="filter-certifications">
                  <AccordionTrigger>
                    Zertifizierungen / Mitgliedschaften
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-7 text-balance">
                    <CheckboxFilterOptions
                      onValueChange={(val) => {
                        const currentCertifications = value.filters
                          .certifications ?? {
                          gstcHotel: false,
                          greenKey: false,
                          iso14001: false,
                        };

                        const updated = { ...currentCertifications };

                        if (val.itemId === 1) {
                          updated.gstcHotel = !!val.value;
                        } else if (val.itemId === 2) {
                          updated.greenKey = !!val.value;
                        } else if (val.itemId === 3) {
                          updated.iso14001 = !!val.value;
                        }

                        onValueChange({
                          ...value,
                          filters: {
                            ...value.filters,
                            certifications: updated,
                          },
                        });
                      }}
                      categories={[
                        {
                          id: "certifications",
                          label: "Zertifizierungen & Mitgliedschaften",
                          items: [
                            {
                              id: 1,
                              label:
                                "GSTC Hotel (Global Sustainable Tourism Council)",
                              selected:
                                value.filters.certifications?.gstcHotel ??
                                false,
                            },
                            {
                              id: 2,
                              label: "Green Key",
                              selected:
                                value.filters.certifications?.greenKey ?? false,
                            },
                            {
                              id: 3,
                              label: "ISO 14001",
                              selected:
                                value.filters.certifications?.iso14001 ?? false,
                            },
                          ],
                        },
                      ]}
                    />
                  </AccordionContent>
                </AccordionItem> */}
              </Accordion>
            </TabsScrollArea>
            <div className="mt-6 flex justify-between">
              <Button
                onClick={() => {
                  onValueChange({
                    filters: {
                      occasion: [],
                      locationType: [],
                      seating: "",
                      locationSettings: {
                        indoor: false,
                        outdoor: false,
                        mixed: false,
                      },
                      locationSize: {
                        maxArea: 0,
                        indoorAreaTotal: 0,
                        maxHeight: 0,
                      },
                      locationOpening: 0,
                      locationRenovation: 0,
                      commonProperties: {
                        accessible: false,
                        dimmable: false,
                        daylight: false,
                      },
                      numberOfNeededRooms: undefined,
                      outerAreaSize: 0,
                      stage: false,
                      numberOfParkingSpaces: 0,
                      parkingLots: undefined,
                      hasAccommodations: undefined,
                      hotelRooms: 0,
                      hotelStars: 0,
                      hotelTypes: [],
                      budget: undefined,
                      cateringBinding: "",
                      techBinding: "",
                      infrastructure: [],
                      distances: [],
                      rentalDuration: {
                        days: [],
                        from: null,
                        to: null,
                      },
                      deliveryLogistics: {
                        truckAccess: false,
                        carAccess: false,
                        freightElevator: false,
                        loadingRamp: false,
                      },
                      leisureSportWellness: {
                        leisure: {
                          sightseeing: false,
                        },
                        sport: {
                          golf: false,
                          tennis: false,
                          sailing: false,
                          ski: false,
                        },
                        wellness: {
                          sauna: false,
                          massage: false,
                          indoorPool: false,
                          outdoorPool: false,
                        },
                      },
                      certifications: {
                        gstcHotel: false,
                        greenKey: false,
                        iso14001: false,
                      },
                      equipment: {
                        acPower: {
                          volt110: false,
                          volt230: false,
                        },
                        threePhasePower: {
                          amp16: false,
                          amp32: false,
                          amp64: false,
                          amp128: false,
                        },
                        technology: {
                          lighting: false,
                          presentationVideo: false,
                          audio: false,
                          heatingClimate: false,
                        },
                        specialTechnology: {
                          rigging: false,
                          v1: false,
                          v2: false,
                          v3: false,
                          v4: false,
                          v5: false,
                        },
                        internet: {
                          g5: false,
                          g4: false,
                          g3: false,
                          g2: false,
                          minBandwidth: 0,
                        },
                        furniture: "none",
                        sanitary: {
                          wcWomen: false,
                          wcMen: false,
                          wcAccessible: false,
                        },
                        other: {
                          smokingArea: false,
                          artistDressingRooms: false,
                          noNoiseRestrictions: false,
                          shower: false,
                          backstageArea: false,
                          danceFloor: false,
                        },
                      },
                    },
                    guests: "",
                    type: null,
                  });
                }}
                variant={"link"}
                className="text-red-dark"
              >
                Filter löschen
              </Button>
              <DialogClose asChild>
                <Button variant={"black"}>
                  <AmountFormatted>{numberOfMatchingLocations}</AmountFormatted>{" "}
                  Ergebnisse anzeigen
                </Button>
              </DialogClose>
            </div>
          </TabsContent>
          <TabsContent value="aiSearch"></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function CheckboxFilterOptions({
  categories,
  onValueChange,
}: {
  categories: {
    id: string;
    label: string;
    items: {
      id: number;
      label: string;
      selected: boolean;
      amount?: number;
    }[];
  }[];
  onValueChange: ({
    categoryId,
    itemId,
  }: {
    categoryId: string;
    itemId: number;
    value: boolean;
  }) => void;
}) {
  return categories.map((category) => (
    <fieldset className="flex flex-col" key={category.id}>
      <legend className="font-semibold text-lg mb-3">{category.label}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {category.items.map((item) => (
          <div key={item.id} className="flex items-center gap-3">
            <Checkbox
              id={`${category.id}-${item.id}`}
              checked={item.selected}
              onCheckedChange={(checked) => {
                onValueChange({
                  categoryId: category.id,
                  itemId: item.id,
                  value: !!checked,
                });
              }}
            />
            <Label className="text-base" htmlFor={`${category.id}-${item.id}`}>
              {item.label}
              {item.amount !== undefined && ` (${item.amount})`}
            </Label>
          </div>
        ))}
      </div>
    </fieldset>
  ));
}

type SeatingSelectValue =
  | "party"
  | "standing-reception"
  | "block"
  | "banquet"
  | "row"
  | "parliament"
  | "u-shaped"
  | "table-seating";

function SeatingSelect({
  value,
  onValueChange,
}: {
  value: SeatingSelectValue;
  onValueChange: (value?: SeatingSelectValue) => void;
}) {
  const seatings: Array<{
    id: SeatingSelectValue;
    label: string;
    icon: string;
    selected: string;
  }> = [
      {
        id: "party",
        label: "Party",
        icon: partySeatingIcon,
        selected: partySeatingIconSelected,
      },
      {
        id: "standing-reception",
        label: "Stehempfang",
        icon: standingReceptionIcon,
        selected: standingReceptionIconSelected,
      },
      {
        id: "block",
        label: "Block",
        icon: blockIcon,
        selected: blockIconSelected,
      },
      {
        id: "banquet",
        label: "Bankett",
        icon: banquetIcon,
        selected: banquetIconSelected,
      },
      {
        id: "row",
        label: "Reihenbestuhlung",
        icon: rowIcon,
        selected: rowIconSelected,
      },
      {
        id: "parliament",
        label: "Parlamentarische Bestuhlung",
        icon: parliamentIcon,
        selected: parliamentIconSelected,
      },
      {
        id: "u-shaped",
        label: "U-Form",
        icon: uShapedIcon,
        selected: uShapedIconSelected,
      },
      {
        id: "table-seating",
        label: "Tafelbestuhlung",
        icon: tableSeatingIcon,
        selected: tableSeatingIconSelected,
      },
    ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {seatings.map((seating) => (
        <div
          key={seating.id}
          className="flex items-center gap-5 group/seat cursor-pointer"
          data-selected={value === seating.id}
          onClick={() => {
            if (value !== seating.id) {
              onValueChange(seating.id as SeatingSelectValue);
            } else {
              onValueChange(undefined);
            }
          }}
        >
          <img
            src={seating.icon}
            alt=""
            className="rounded-md size-14 block group-data-[selected=true]/seat:hidden group-hover/seat:hidden"
          />
          <img
            src={seating.selected}
            alt=""
            className="rounded-md size-14 hidden group-data-[selected=true]/seat:block group-hover/seat:block"
          />
          {seating.label}
        </div>
      ))}
    </div>
  );
}
